import { useCallback } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import { useReplaySessionStore } from '../store/replaySessionStore';
import { useTargetId } from '../store/projectStore';
import {
  buildReplaySessionPayload,
  focusReplayView,
  type ReplayOpenRequestInput,
  type ReplayOpenRequestOptions,
} from '../utils/replayOpenRequest.utils';
import { looksLikeDisplayedBinaryMultipart } from '../utils/replayRequestSession.utils';
import type { BodyEncoding } from '../electron-shared/utils/binary-body';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';

export type { ReplayOpenRequestInput, ReplayOpenRequestOptions };

type TrackedHarRequest = {
  method: string;
  url: string;
  headers: { request: Record<string, string | string[]> };
  rawHeaders: { request?: string[] };
  requestBody?: unknown;
  requestBodyEncoding?: BodyEncoding;
  httpVersion?: string;
  grpcHarDisplay?: StoredHarDisplayEntry;
};

function stringifyMaybeJsonBody(body: unknown): string {
  if (body === null || body === undefined) {
    return '';
  }

  if (typeof body === 'string') {
    return body;
  }

  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

export function useHttpReplay() {
  const { addSession, loadRequestIntoSession } = useReplaySessionStore();
  const targetId = useTargetId();

  const openInReplay = useCallback(
    (
      requestData: ReplayOpenRequestInput,
      options?: ReplayOpenRequestOptions,
    ) => {
      try {
        if (!requestData?.url) {
          console.error(
            'openInReplay: Missing required url field',
            requestData,
          );
          toasterApi.error({
            title: 'Failed to open replay',
            description: 'Invalid request data: missing URL',
          });

          return;
        }

        const { activeCollectionId } = useReplaySessionStore.getState();
        const sessionPayload = buildReplaySessionPayload({
          requestData,
          applicationId: targetId,
          collectionId: activeCollectionId,
          looksLikeDisplayedBinaryMultipart,
        });

        if (options?.sessionId) {
          loadRequestIntoSession({
            sessionId: options.sessionId,
            session: sessionPayload,
          });
        } else {
          addSession(sessionPayload);
        }

        focusReplayView();
      } catch (error) {
        console.error('Error opening replay:', error);
        toasterApi.error({
          title: 'Failed to open replay',
          description: 'Please try again',
        });
      }
    },
    [addSession, targetId, loadRequestIntoSession],
  );

  const openHarRequestInReplay = useCallback(
    (request: TrackedHarRequest, options?: ReplayOpenRequestOptions) => {
      const requestBody = request.requestBody
        ? stringifyMaybeJsonBody(request.requestBody) || ''
        : '';

      openInReplay(
        {
          method: request.method,
          url: request.url,
          headers: request.headers.request,
          rawHeaders: request.rawHeaders.request,
          body: requestBody,
          bodyEncoding: request.requestBodyEncoding,
          httpVersion: request.httpVersion,
          grpcHarDisplay: request.grpcHarDisplay,
        },
        options,
      );
    },
    [openInReplay],
  );

  return { openInReplay, openHarRequestInReplay };
}
